import { useState, useRef, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Button, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEhrContext } from '../contexts/ehr-context';
import { useSessionContext } from '../contexts/session-context';

const useStyles = makeStyles(theme => ({
  menuItem: {
    fontWeight: 600,
    fontSize: '14px',
    '&.Mui-disabled': {
      opacity: 1,
    },
  },
  menuSubItem: {
    fontSize: '14px',
    paddingLeft: theme.spacing(4),
    fontWeight: 600,
  },
  menuDeliveryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  menuDeliveryTitle: {
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1.3,
  },
  menuDeliverySubtitle: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: '#6b7280',
    fontWeight: 500,
    whiteSpace: 'normal',
  },
  newBookButton: {
    width: '100%',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#7c5e90',
      color: 'white',
    },
  },
  secondaryNewBookButton: {
    marginRight: theme.spacing(2),
    '&.Mui-disabled': {
      backgroundColor: '#92C9D6BF',
      color: 'white',
    },
    '&.MuiButton-contained.Mui-disabled:hover': {
      backgroundColor: '#92C9D6BF', // prevent the default hover
    },
  },
}));

const NewBookButton = ({
  useSecondaryButton,
}: {
  useSecondaryButton: boolean;
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { session } = useSessionContext();
  const profile = session?.profile;
  let { riderId } = useParams<{ riderId?: string }>();
  const { ehrSession } = useEhrContext();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const ambulanceBookFeatureFlag =
    session?.featureFlags?.includes('portal_book_ambulance') === true;
  const gurneyBookFeatureFlag =
    session?.featureFlags?.includes('portal_book_gurney') === true;
  const wheelchairBookFeatureFlag =
    session?.featureFlags?.includes('portal_book_wheelchair') === true;

  const accountTransportTypes = profile?.availableTransportTypes || [];
  let availableTransportTypes: any[] = [];

  accountTransportTypes.forEach(transportType => {
    if (['companion', 'rideshare'].includes(transportType.name)) {
      // "companion" and "rideshare" are already supported by Portal.
      availableTransportTypes.push(transportType);
    } else if (transportType.name === 'gurney' && gurneyBookFeatureFlag) {
      availableTransportTypes.push(transportType);
    } else if (
      transportType.name === 'wheelchair' &&
      wheelchairBookFeatureFlag
    ) {
      availableTransportTypes.push(transportType);
    } else if (
      ['bls_ambulance', 'cct_ambulance', 'als_ambulance'].includes(
        transportType.name,
      ) &&
      ambulanceBookFeatureFlag
    ) {
      availableTransportTypes.push(transportType);
    }
  });

  const toggleDropdown = () => {
    setOpen((prev: boolean) => !prev);
  };

  const buildBookUrl = (transportType: string) => {
    const basePath = `/rides/newbook/${transportType}`;
    const fullPath = riderId ? `${basePath}/${riderId}` : basePath;
    const params = new URLSearchParams();
    const encounter = ehrSession?.ehrAccessToken?.encounter;

    if (encounter) {
      params.set('ehrEncounterId', encounter);
    }

    const qs = params.toString();
    return qs ? `${fullPath}?${qs}` : fullPath;
  };

  const handleSelectRideType = (type: string) => {
    if (type) {
      history.push(buildBookUrl(type));
    }
    setOpen(false);
  };

  interface DeliveryTransportType {
    __typename: string;
    id: null;
    fullName: string;
    name: string;
    shortName: string;
    description: string;
  }

  let deliveryTransportTypes: DeliveryTransportType[] = [];

  if (availableTransportTypes.some(t => t.name === 'companion')) {
    deliveryTransportTypes = [
      {
        __typename: 'Transport',
        id: null,
        fullName: 'Standard Item',
        name: 'delivery-companion',
        shortName: 'Standard Item',
        description: 'Transported by Companion Driver',
      },
    ];
  }
  if (availableTransportTypes.some(t => t.name === 'wheelchair')) {
    deliveryTransportTypes = [
      ...deliveryTransportTypes,
      {
        __typename: 'Transport',
        id: null,
        fullName: 'Specialized Item',
        name: 'delivery-wheelchair',
        shortName: 'Specialized Item',
        description: 'Transported by Wheelchair Van',
      },
    ];
  }
  const bookButtonColor = useSecondaryButton ? 'secondary' : 'primary';
  const newBookButton = useSecondaryButton
    ? classes.secondaryNewBookButton
    : classes.newBookButton;

  return availableTransportTypes.length === 1 ? (
    <Button
      className={newBookButton}
      variant="contained"
      color={bookButtonColor}
      component={Link}
      to={buildBookUrl(availableTransportTypes[0].name)}
    >
      Book New
    </Button>
  ) : (
    <>
      <Button
        className={newBookButton}
        ref={anchorRef}
        variant="contained"
        color={bookButtonColor}
        disabled={open}
        onClick={toggleDropdown}
      >
        Book New +
      </Button>

      <Menu
        anchorEl={anchorRef.current}
        getContentAnchorEl={null}
        anchorOrigin={
          useSecondaryButton
            ? {
                vertical: 'bottom',
                horizontal: 'right',
              }
            : {
                vertical: 'bottom',
                horizontal: 'left',
              }
        }
        transformOrigin={
          useSecondaryButton
            ? {
                vertical: 'top',
                horizontal: 'right',
              }
            : {
                vertical: 'top',
                horizontal: 'left',
              }
        }
        keepMounted
        open={open}
        onClose={handleClose}
        PaperProps={
          /* For the primary button, we want the menu to be the same width as the button. */
          useSecondaryButton
            ? {}
            : {
                style: {
                  width: anchorRef.current
                    ? anchorRef.current.offsetWidth
                    : undefined,
                },
              }
        }
      >
        {/* Ride Section */}
        {availableTransportTypes.length > 0
          ? [
              <MenuItem key="ride-header" disabled className={classes.menuItem}>
                Ride
              </MenuItem>,
              ...availableTransportTypes.map(({ fullName, name }) => (
                <MenuItem
                  key={`ride-${name}`}
                  onClick={() => handleSelectRideType(name)}
                  className={classes.menuSubItem}
                >
                  {fullName}
                </MenuItem>
              )),
            ]
          : null}
        {/* Delivery Section */}
        {deliveryTransportTypes.length > 0
          ? [
              <MenuItem
                key="delivery-header"
                disabled
                className={classes.menuItem}
              >
                Delivery
              </MenuItem>,
              ...deliveryTransportTypes.map(
                ({ fullName, name, description }) => (
                  <MenuItem
                    key={`delivery-${name}`}
                    onClick={() => handleSelectRideType(name)}
                    className={classes.menuDeliveryItem}
                  >
                    <div className={classes.menuDeliveryTitle}>{fullName}</div>
                    <div className={classes.menuDeliverySubtitle}>
                      {description}
                    </div>
                  </MenuItem>
                ),
              ),
            ]
          : null}
      </Menu>
    </>
  );
};

export default NewBookButton;
